import {
  AfterViewInit,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  input,
  ResourceStatus,
  signal,
  ViewChild
} from '@angular/core';
import {DOCUMENT, NgIf} from '@angular/common';
import {AuthByPhoneComponent} from '../../common/form/auth-by-phone/auth-by-phone.component';
import {PaypalComponent} from '../../common/paypal/paypal.component';
import {PaymentButtonComponent} from '../../common/button/payment-button/payment-button.component';
import {ShowPricePipe} from '../../../../common/pipe/showPrice.pipe';
import {FIREBASE_AUTH} from '../../../config/module.di.tokens';
import {SubscriptionService} from '../../../../services/subscription.service';
import {SubscriptionResources} from '../../../../resources/subscription.resources';
import {LeadResources} from '../../../../resources/lead.resources';
import {FormBuilder} from '@angular/forms';
import {Router, RouterLink} from '@angular/router';
import {
  PaySubscriptionInPlace
} from '../../common/subscription-confirmation/payment/subscription.confirmation.pay-in-place';
import {OfferSubscription} from '../../common/subscription-confirmation/payment/subscription.confirmation.offer';
import {
  PaySubscriptionByBankTransfer
} from '../../common/subscription-confirmation/payment/subscription.confirmation.bank-transfer';
import {PaySubscriptionByPaypal} from '../../common/subscription-confirmation/payment/subscription.confirmation.paypal';
import {Option, PaymentContext} from '../../../../model/subscription.model';
import {MyPaypalConfig} from '../../../../model/paypal.model';
import {
  SubscriptionConfirmationContext
} from '../../common/subscription-confirmation/subscription.confirmation.context';
import {
  SubscriptionConfirmationActions
} from '../../common/subscription-confirmation/subscription.confirmation.actions';
import {SubscriptionContextV2Service} from '../../../../services/subscription-v2-context.service';
import {addDefaultAnimationIfNeeded} from '../../../helper/lead.helper';
import {Lead} from '../../../../data/domain/lead';
import {ErrorReportingApi} from '../../../../common/api/error-reporting.api';
import {SessionResources} from '../../../../resources/session.resources';
import {LpButtonComponent} from '../../common/lp/lp-button/lp-button.component';
import {SessionDTO, Sex} from '@minutespourtoi/api-models/dist/models';

@Component({
  selector: 'app-sub-v2-pay',
  imports: [
    NgIf,
    AuthByPhoneComponent,
    PaypalComponent,
    PaymentButtonComponent,
    ShowPricePipe,
    LpButtonComponent,
    RouterLink
  ],
  templateUrl: './sub-v2-pay.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SubV2PayComponent implements AfterViewInit {

  sessionId = input<string>()
  referralCode = input<string>()
  paymentContext = signal<PaymentContext>(new PaymentContext())
  sessionResources = inject(SessionResources)
  document = inject(DOCUMENT)
  auth = inject(FIREBASE_AUTH);
  subscriptionService = inject(SubscriptionService)
  subscriptionContextV2Service = inject(SubscriptionContextV2Service)
  subscriptionResources = inject(SubscriptionResources)
  leadResources = inject(LeadResources)
  formBuilder = inject(FormBuilder)
  router = inject(Router)
  payInPlacePayment = inject(PaySubscriptionInPlace)
  offerSubscriptionPayment = inject(OfferSubscription)
  bankTranferPayment = inject(PaySubscriptionByBankTransfer)
  paypalPayment = inject(PaySubscriptionByPaypal)
  errorReportingApi = inject(ErrorReportingApi);
  options = new Array<Option>()
  payPalConfig?: MyPaypalConfig;
  phoneAlreadyUsed = computed(() => (this.subscriptionResources.calculatePrice.error() as any)?.status === 412)

  context = {
    paymentContext: this.paymentContext
  } as SubscriptionConfirmationContext
  actions = inject(SubscriptionConfirmationActions)

  @ViewChild(AuthByPhoneComponent)
  authByPhone: AuthByPhoneComponent;

  ngAfterViewInit(): void {
    this.payInPlacePayment.setAuthByPhone(this.authByPhone)
    this.offerSubscriptionPayment.setAuthByPhone(this.authByPhone)
    this.bankTranferPayment.setAuthByPhone(this.authByPhone)
    this.paypalPayment.setAuthByPhone(this.authByPhone)
  }

  constructor() {

    effect(async () => {
      const sessionStatus = this.sessionResources.session.status()
      const leadStatus = this.leadResources.currentLead.status()

      if (sessionStatus == ResourceStatus.Resolved && leadStatus == ResourceStatus.Resolved) {
        const session = this.sessionResources.session.value()
        const lead = this.leadResources.currentLead.value()

        if(session) {
          this.actions.setUp(this.context, session, this.referralCode())
          this.payInPlacePayment.setUp(session, this.actions, this.context, this.referralCode())
          this.offerSubscriptionPayment.setUp(session, this.actions, this.context, this.referralCode())
          this.bankTranferPayment.setUp(session, this.actions, this.context, this.referralCode())
          this.paypalPayment.setUp(session, this.actions, this.context, this.referralCode())
          if(lead) {
            await this.setUp(lead, session)
          }
        }
      }
    });
    effect(() => {
      const priceStatus = this.subscriptionResources.calculatePrice.status()
      if (priceStatus == ResourceStatus.Resolved) {
        const price = this.subscriptionResources.calculatePrice.value()
        if (price) {
          this.initPaymentConfig(price.totalPrice)
        }
      }
    });
    effect(() => {
      const paymentContext = this.paymentContext()
      if (paymentContext?.error) {
        this.scrollToTop()
      }
    });
  }

  async setUp(leadDTO: Lead, sessionDTO: SessionDTO) {
    if (leadDTO) {
      await addDefaultAnimationIfNeeded(leadDTO, sessionDTO)
      if (!leadDTO?.isValidForConfirmation()) {
        this.errorReportingApi.report({
          leadDTO : leadDTO,
          error: "Invalid Lead DTO at payment",
        })
        await this.router.navigate(['/subscribe-v2', this.sessionId(), 'who'])
      } else {
        if (!leadDTO.offer) {
          leadDTO.offer = await this.subscriptionContextV2Service.detectDefaultOffer()
        }
        if (!this.subscriptionResources.calculatePrice.hasValue()) {
          this.recalculatePrice(leadDTO)
        }
      }
    }
  }

  recalculatePrice(leadDTO: Lead) {

    this.subscriptionResources.calculatePriceRequest.set({
      options: leadDTO?.options.map(opt => opt.code),
      sex: leadDTO.sex as Sex,
      discountCode: leadDTO.discount?.discountCode,
      sessionId: leadDTO.sessionId,
      subscriptionId : leadDTO.subscription?.id,
      firstname: leadDTO.firstname,
      offerCode: leadDTO.offer.code,
      phoneNumber: leadDTO.phone,
    })
  }

  private initPaymentConfig(precalculatedPrice: number): void {
    console.log("pass")
    this.payPalConfig = {
      refundable: true,
      preCalculatedPrice: precalculatedPrice,
      customCardFilling : true,
      payLaterOption: false,
      createOrderOnServer: async (data) => this.paypalPayment.pay(data['paymentSource']),
      onApprove: () => this.paypalPayment.capturePayment(),
      onCancel: async () => this.paymentContext.set({
        loaded: false,
        loading: false,
        error: null,
        orderCreated: false,
        captured: false,
        authenticationInProgress: false
      }),
      onError: async err => {
        if (!this.paymentContext()?.error && err?.message) {
          this.actions.failWithTechnicalError(err)
        }
      }
    }
  }

  scrollToTop() {
    const mainScrollElement = this.document.querySelector(".main-scroll") as HTMLElement;
    mainScrollElement.scrollTo({behavior: 'smooth', top: 0});
  }

}
